/**
 * Placeholder shown while a node's markers are still being fetched.
 *
 * The tiles themselves are available instantly — they come from the node's
 * history in YDoc. What is NOT available is which TAB each one belongs to, and
 * that only the server knows. Because an absent marker means "starred",
 * rendering the grid before the fetch resolves does not degrade gracefully: it
 * asserts a specific wrong answer (every tile under Variants, the chips reading
 * "2 / 0") and then visibly corrects itself.
 *
 * So the marker-dependent chrome waits. This occupies the same footprint the
 * chip row and grid will, so the panel does not resize when the real content
 * arrives.
 *
 * Only reachable when the exposure gate is ON and nothing is cached for this
 * node yet — in practice, the first open after a page load. Within a session
 * the module-level cache in `useVariantMarkers` paints immediately instead.
 */

import { memo } from 'react'
import { ShimmerPlaceholder } from './ShimmerPlaceholder'
import { VARIANT_TAB_ROW_HEIGHT } from './VariantTabChips'

export interface VariantsGridSkeletonProps {
  /** How many tile placeholders to draw — the node's known variant count. */
  tileCount: number
  /** Grid column count, matched to the real grid so nothing shifts. */
  columns: number
  /** CSS aspect-ratio string for each tile, e.g. "16/9". */
  aspectRatio: string
  /** Optional per-tile max width, matched to the real grid. */
  maxWidth?: number
}

const VariantsGridSkeleton = memo(function VariantsGridSkeleton({
  tileCount,
  columns,
  aspectRatio,
  maxWidth,
}: VariantsGridSkeletonProps) {
  return (
    <div data-testid="variants-grid-skeleton">
      {/* Stand-in for the chip row: same height, deliberately no numbers. */}
      <div
        className="flex items-center gap-1.5 mb-2"
        style={{ height: VARIANT_TAB_ROW_HEIGHT - 8 }}
      >
        <ShimmerPlaceholder width={92} height={24} />
        <ShimmerPlaceholder width={92} height={24} />
      </div>

      <div
        className="grid gap-2"
        style={{ gridTemplateColumns: `repeat(${columns}, 1fr)` }}
      >
        {Array.from({ length: tileCount }).map((_, i) => (
          <div
            key={i}
            className="rounded-lg overflow-hidden bg-surface-2"
            style={{ aspectRatio, width: '100%', ...(maxWidth ? { maxWidth } : {}) }}
          >
            <ShimmerPlaceholder width="100%" height="100%" />
          </div>
        ))}
      </div>
    </div>
  )
})

VariantsGridSkeleton.displayName = 'VariantsGridSkeleton'

export { VariantsGridSkeleton }
